import React, { JSX, useMemo, useState } from 'react';
import { Box, Card, CardBody, Heading, HStack, Text } from '@chakra-ui/react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useTranslations } from '../../../contexts/LocalizationContext';
import { WeeklyStatisticsResponse } from '../model/WeeklyStatisticsResponse';
import { formatWeekLabel, MetricKey, METRICS, ThresholdMap } from '../utils/metrics';
import { createAnomalyDot } from './AnomalyDot';
import ChartTooltip from './ChartTooltip';

interface Props {
  weeks: WeeklyStatisticsResponse[];
  thresholdMap: ThresholdMap;
}

const ALL_KEYS = new Set<MetricKey>(METRICS.map((m) => m.key));

const MetricsChart = ({ weeks, thresholdMap }: Props): JSX.Element => {
  const translations = useTranslations();
  const [activeKeys, setActiveKeys] = useState<Set<MetricKey>>(new Set(ALL_KEYS));

  const toggleMetric = (key: MetricKey) => {
    setActiveKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) return new Set(ALL_KEYS);
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const chartData = weeks.map((w) => ({
    weekLabel: formatWeekLabel(w.weekStart),
    weekStart: w.weekStart,
    weekEnd: w.weekEnd,
    weekNumber: w.weekNumber,
    ...Object.fromEntries(METRICS.map((m) => [m.key, w[m.key]])),
  }));

  const anomalyDots = useMemo(
    () =>
      Object.fromEntries(METRICS.map((m) => [m.key, createAnomalyDot(thresholdMap[m.key])])) as Record<
        MetricKey,
        ReturnType<typeof createAnomalyDot>
      >,
    [thresholdMap],
  );

  const singleActiveMetric = activeKeys.size === 1 ? METRICS.find((m) => activeKeys.has(m.key)) : undefined;
  const singleActiveThreshold = singleActiveMetric ? thresholdMap[singleActiveMetric.key] : undefined;

  return (
    <Card borderRadius="xl">
      <CardBody>
        <Heading size="sm" mb={4}>
          {translations('monitoring_chart_title')}
        </Heading>
        <HStack spacing={5} mb={3} flexWrap="wrap">
          {METRICS.map((m) => {
            const isActive = activeKeys.has(m.key);
            return (
              <HStack
                key={m.key}
                spacing={2}
                cursor="pointer"
                opacity={isActive ? 1 : 0.4}
                onClick={() => toggleMetric(m.key)}
              >
                <Box w="8px" h="8px" borderRadius="full" bg={m.color} />
                <Text fontSize="sm" color="grey.500">
                  {translations(m.translationKey)}
                </Text>
              </HStack>
            );
          })}
        </HStack>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="weekLabel" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip content={<ChartTooltip thresholdMap={thresholdMap} translations={translations} />} />
            {METRICS.map((m) =>
              activeKeys.has(m.key) ? (
                <Line
                  key={m.key}
                  type="monotone"
                  dataKey={m.key}
                  stroke={m.color}
                  strokeWidth={2}
                  dot={anomalyDots[m.key]}
                />
              ) : null,
            )}
            {singleActiveMetric && singleActiveThreshold && (
              <>
                <ReferenceLine
                  y={singleActiveThreshold.thresholdUp}
                  stroke={singleActiveMetric.color}
                  strokeDasharray="6 4"
                  strokeOpacity={0.5}
                />
                <ReferenceLine
                  y={singleActiveThreshold.thresholdDown}
                  stroke={singleActiveMetric.color}
                  strokeDasharray="6 4"
                  strokeOpacity={0.5}
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
};

export default MetricsChart;
